import type { RoutingMot } from "mobility-toolbox-js/types";

import type { GraphhopperRoutingProfile } from "../components/Editor/GraphhopperRoutingControl";

/* OpenLayers */
export const OL_ICON_CACHE_SIZE = 2000;

/* Route */
export const PREVIEW_PATHNAME = "/plans";
export const DEFAULTS_PATHNAME = "/defaults";

/* PDF layout params */
export const LayoutVariableKey = {
  MAPSET_LAYOUT_DESCRIPTION: "MAPSET_LAYOUT_DESCRIPTION",
  MAPSET_LAYOUT_FILENAME: "MAPSET_LAYOUT_FILENAME",
  MAPSET_LAYOUT_MAP: "MAPSET_LAYOUT_MAP",
  MAPSET_LAYOUT_TITLE: "MAPSET_LAYOUT_TITLE",
};
export type LayoutVariableKey =
  (typeof LayoutVariableKey)[keyof typeof LayoutVariableKey];
export const MAPSET_LAYOUT_TITLE = LayoutVariableKey.MAPSET_LAYOUT_TITLE;
export const MAPSET_LAYOUT_MAP = LayoutVariableKey.MAPSET_LAYOUT_MAP;
export const MAPSET_LAYOUT_DESCRIPTION =
  LayoutVariableKey.MAPSET_LAYOUT_DESCRIPTION;
export const MAPSET_LAYOUT_FILENAME = LayoutVariableKey.MAPSET_LAYOUT_FILENAME;

/* Extra (non-template-variable) query params sent alongside MAPSET_LAYOUT_MAP
 * so the backend can build a screenshot request that matches the export's
 * actual size, instead of always falling back to the layout's own nominal
 * map dimensions. */
export const MAPSET_LAYOUT_MAP_WIDTH = "MAPSET_LAYOUT_MAP_WIDTH";
export const MAPSET_LAYOUT_MAP_HEIGHT = "MAPSET_LAYOUT_MAP_HEIGHT";
export const MAPSET_LAYOUT_MAP_SCALE = "MAPSET_LAYOUT_MAP_SCALE";

/* Mapset environment */
export const ENV_DEV = "dev";
export const ENV_STAG = "stag";
export const ENV_PROD = "prod";

/* Mapset domains */
export const DOMAIN_CH = "ch";
export const DOMAIN_IO = "io";
export const DOMAIN_APP = "app";
export const DOMAIN_BERNMOBIL = "bernmobil";
export const HostnameDomain = {
  App: DOMAIN_APP,
  Bernmobil: DOMAIN_BERNMOBIL,
  Ch: DOMAIN_CH,
  Io: DOMAIN_IO,
} as const;
export type HostnameDomain =
  (typeof HostnameDomain)[keyof typeof HostnameDomain];

/* Mapset domains that have a configuration (bernomobil is just a redirection to CH*/
export const Domain = {
  App: DOMAIN_APP,
  Ch: DOMAIN_CH,
  Io: DOMAIN_IO,
} as const;
export type Domain = (typeof Domain)[keyof typeof Domain];

/* Mapset hostnames */
export const HOSTNAMES_BY_ENV = {
  // DEV
  dev_app: "https://editor.dev.mapset.app",
  dev_bernmobil: "https://karten.dev.bernmobil.ch",
  dev_ch: "https://editor.dev.mapset.ch",
  dev_io: "https://editor.dev.mapset.io",
  // PROD
  prod_app: "https://editor.mapset.app",
  prod_bernmobil: "https://karten.bernmobil.ch",
  prod_ch: "https://editor.mapset.ch",
  prod_io: "https://editor.mapset.io",
  // STAG
  stag_app: "https://editor.stag.mapset.app",
  stag_bernmobil: "https://karten.test.bernmobil.ch",
  stag_ch: "https://editor.stag.mapset.ch",
  stag_io: "https://editor.stag.mapset.io",
};

/* Mapset rights */
export const RIGHT_SET_DEFAULTS =
  /^mapset\.tp\.[A-Za-z0-9]*\.[A-Za-z0-9]*\.defaultplan$/; // Individual providers
export const RIGHT_SET_DEFAULTS_SU = /^mapset\.defaultplans\.setdefaults$/; // Superuser (internal)
export const RIGHT_MODUL_1 = /^geops\.marketing\.mapset_modul1$/; // Superuser (internal)

/* Layer names */
export const INSPECT_LAYER_NAME = "inspectLayer";
export const EDIT_LAYER_NAME = "editLayer";
export const BASE_LAYER_NAME = "basemap";
export const AERIAL_LAYER_NAME = "aerial";
export const SATURATED_LAYER_NAME = "saturated";
export const SEARCH_HIGHLIGHT_LAYER_NAME = "searchHighlightLayer";

/* Search */
export const SEARCH_MIN_QUERY_LENGTH = 2;
/** Search results of the places engine are restricted to Europe (lon/lat). */
export const SEARCH_EUROPE_BBOX: [number, number, number, number] = [
  -31.5, 34.5, 45, 71.5,
];
export const GEOAPIFY_AUTOCOMPLETE_URL =
  "https://api.geoapify.com/v1/geocode/autocomplete";
export const GEOAPIFY_PLACE_DETAILS_URL =
  "https://api.geoapify.com/v2/place-details";

/* Dialog names */
export const LAYERS_DIALOG_NAME = "layers";
export const WARN_DIALOG_NAME = "warn";
export const PLAN_NAME_DIALOG_NAME = "planName";
export const SIGNOUT_DIALOG_NAME = "signout";
export const NEW_PLAN_DIALOG_NAME = "new";
export const SETTINGS_DIALOG_NAME = "settings";
export const ERROR_DIALOG_NAME = "error";
export const GROUP_DIALOG_NAME = "group";
export const SHARE_DIALOG_NAME = "share";
export const SAVE_DIALOG_NAME = "save";
export const EXPORT_DIALOG_NAME = "export";
export const SBBWELCOME_DIALOG_NAME = "welcome";
export const FEATURE_STYLER_DIALOG_NAME = "feature-styler";
export const ROUTE_FINDER_DIALOG_NAME = "route-finder";
export const PICTURE_STYLER_DIALOG_NAME = "picture-styler";
export const PLANS_LIST_DIALOG_NAME = "plans-list";
export const PLAN_DELETE_DIALOG_NAME = "plan-delete";
export const PLAN_CHANGE_DIALOG_NAME = "plan-change";
export const DID_YOU_KNOW_DIALOG_NAME = "did-you-know";
export const SAVE_CONFIRM_DIALOG_NAME = "save-confirm";
export const FEEDBACK_DIALOG_NAME = "feedback";
export const LAYOUT_PROTECTED_DIALOG_NAME = "layout-protected";
export const CUSTOM_LAYOUT_DIALOG_NAME = "custom-layout";
export const RELOAD_DIALOG_NAME = "reload";
export const TAG_CONFIRM_DIALOG_NAME = "tag-confirm";
export const READ_ONLY_DIALOG_NAME = "read-only";
export const MAX_MAPS_REACHED_DIALOG_NAME = "upgrade";
export const MULTI_FEATURES_DIALOG_NAME = "multi-features";
export const ALL_DIALOGS_NAMES = [
  MAX_MAPS_REACHED_DIALOG_NAME,
  LAYERS_DIALOG_NAME,
  WARN_DIALOG_NAME,
  PLAN_NAME_DIALOG_NAME,
  SIGNOUT_DIALOG_NAME,
  NEW_PLAN_DIALOG_NAME,
  SETTINGS_DIALOG_NAME,
  ERROR_DIALOG_NAME,
  GROUP_DIALOG_NAME,
  SHARE_DIALOG_NAME,
  SAVE_DIALOG_NAME,
  EXPORT_DIALOG_NAME,
  SBBWELCOME_DIALOG_NAME,
  FEATURE_STYLER_DIALOG_NAME,
  ROUTE_FINDER_DIALOG_NAME,
  PICTURE_STYLER_DIALOG_NAME,
  PLANS_LIST_DIALOG_NAME,
  PLAN_DELETE_DIALOG_NAME,
  PLAN_CHANGE_DIALOG_NAME,
  DID_YOU_KNOW_DIALOG_NAME,
  SAVE_CONFIRM_DIALOG_NAME,
  FEEDBACK_DIALOG_NAME,
  LAYOUT_PROTECTED_DIALOG_NAME,
  CUSTOM_LAYOUT_DIALOG_NAME,
  RELOAD_DIALOG_NAME,
  TAG_CONFIRM_DIALOG_NAME,
  READ_ONLY_DIALOG_NAME,
  MULTI_FEATURES_DIALOG_NAME,
];

/* Map class name */
export const MAP_CLASS_NAME = "pe-map";

/* Header */
export const HEADER_HEIGHT = 70;

/* Sidebar */
export const SIDEBAR_WIDTH = 50;
export const SIDEBAR_WIDTH_OPEN = 270;
export const DEFAULT_SCROLLBAR_WIDTH = 10;

/* Active Button names */
export const TEXT_BUTTON_NAME = "Text zeichnen";
export const SYMBOL_BUTTON_NAME = "Piktogramm zeichnen";
export const LINE_BUTTON_NAME = "Linie zeichnen";
export const POLYGON_BUTTON_NAME = "Fläche zeichnen";
export const TRIANGLE_BUTTON_NAME = "Dreieck zeichnen";
export const PICTURE_BUTTON_NAME = "Bild einfügen";
export const STOP_FINDER_NAME = "StopFinder";

/* Local storage keys */
export const LS_BBOX_KEY = "pe.bbox.info";
export const LS_KML_KEY = "pe.kml";
export const LS_LANGUAGE_KEY = "i18nextLng";
export const LS_SIZE_KEY = "pe.max.canvas.size";
export const LS_ICON_CAT_COLLASPE = "pe.icon.categories.collapse";
export const LS_EXTENT_KEY = "pe.extent";
export const LS_CD_KEY = "pe.cd";
export const LS_DEFAULT_PROVIDER_KEY = "pe.default.provider";
export const LS_HIDE_SBB_WELCOME = "pe.sbb.welcome";
export const LS_DID_YOU_KNOW_LIST = "pe.did.you.know.list";
export const LS_HIDE_DID_YOU_KNOW = "pe.did.you.know.hide";
export const LS_COUNT_ANON_USE = "pe.count.anon.use";
export const LS_SNAPPING_TYPE = "pe.snapping.type";
export const LS_EXPORT_QR_CODE = "pe.export.qr.code";
export const LS_EXPORT_FORMAT = "pe.export.format";
export const LS_EXPORT_TYPE = "pe.export.type";
export const LS_ACCESSIBLE_ROUTE = "pe.route.barrierfree";
export const LS_EXPORT_BTN_ACTIVE = "pe.export.button.active";
export const LS_GEOLOCATION_ACTIVE = "pe.geolocation.active";
export const LS_ROUTING_MOT = "pe.routing.mot";
export const LS_ORGANIZATION_KEY = "pe.organization";

/* Environments */
export const CH_ENVIRONMENT = "ch";
export const IO_ENVIRONMENT = "io";

/* Anonymous use */
export const MAX_ANON_USE = 3;

/* Editor */
export const HIT_TOLERANCE = 5;
export const SNAP_CONTROL_POINTS = "points";
export const SNAP_CONTROL_LINES = "lines";
export const SNAP_CONTROL_OFF = "off";
export const SnappingType = {
  Lines: SNAP_CONTROL_LINES,
  Off: SNAP_CONTROL_OFF,
  Points: SNAP_CONTROL_POINTS,
} as const;
export type SnappingType = (typeof SnappingType)[keyof typeof SnappingType];

/* Styles */
export const LINE_NODE_DISPLAY_THRESHOLD = 50;
export const PICTURES_Z_INDEX = -1;
export const DRAWING_Z_INDEX = 10000;
export const ICON_SIZES = [
  {
    id: "klein",
    scale: (1 / 144) * 26, // 26px, official max size in der style
    value: [26, 26],
  },
  {
    id: "mittel",
    scale: 1 / 4, // 144px / 4 = 36px
    value: [36, 36],
  },
  {
    id: "gross",
    scale: 1 / 3, // 144px / 3 = 144px
    value: [48, 48],
  },
];
export const POLYGON_LINE_WIDTHS = [
  { id: "very-thin-line", value: 2 },
  { id: "thin-line", value: 4 },
  { id: "medium-line", value: 6 },
  { id: "thick-line", value: 8 },
];

/* Feature property keys */
export const OLD_STYLES_FUNCTION_PROP = "oldStyleFunction";
export const TEXT_BACKGROUND_OPACITY = "textBackgroundOpacity";
export const TEXT_STROKE_OPACITY = "textStrokeOpacity";
export const POLYGON_STROKE_OPACITY = "polygonStrokeOpacity";
export const POLYGON_FILL_OPACITY = "polygonFillOpacity";
export const LINE_STROKE_OPACITY = "lineStrokeOpacity";

/* Pictures */
export const DEFAULT_PIC_SCALE = 1 / 6;

/* Mapbox */

export const StyleMetadataFilterKey = {
  GeneralClass: "general.class",
  GeneralFloor: "general.floor",
  GeneralMot: "general.mot",
  GeneralSubclass: "general.subclass",
  MapsetFilter: "mapset.filter",
} as const;
export type StyleMetadataFilterKey =
  (typeof StyleMetadataFilterKey)[keyof typeof StyleMetadataFilterKey];

export const StyleMetadataFilterValue = {
  Corridors: "corridors",
  MapsetHighlightBus: "mapset_highlight_bus",
  MapsetHighlightTram: "mapset_highlight_tram",
  MotBus: "bus",
  MotCableway: "cableway",
  MotFerry: "ferry",
  MotRail: "rail",
  MotSubway: "subway",
  MotTram: "tram",
  Platforms: "platforms",
  Railways: "railways",
  Rooms: "rooms",
  StationPlan: "station_plan",
} as const;
export type StyleMetadataFilterValue =
  (typeof StyleMetadataFilterValue)[keyof typeof StyleMetadataFilterValue];

export const STYLE_REVIEW_PREFIX =
  import.meta.env.VITE_STYLE_REVIEW_PREFIX || "";
export const MAPSET_BUS_DATA_SOURCE = "busses"; // we display popup only using if the bus layer use this data source
export const MAPSET_METADATA_FILTER = "mapset.filter";
export const MOT_METADATA_FILTER = "general.mot";
export const GENERAL_METADATA_FILTER = "general.class";
export const MAPSET_METADATA_BUS_VALUE = "mapset_highlight_bus";
export const MAPSET_METADATA_TRAM_VALUE = "mapset_highlight_tram";
export const MOT_METADATA_BUS_VALUE = "bus";
export const MOT_METADATA_TRAM_VALUE = "tram";
export const MOT_METADATA_FERRY_VALUE = "ferry";
export const MOT_METADATA_SUBWAY_VALUE = "subway";
export const MOT_METADATA_RAIL_VALUE = "rail";
export const MOT_METADATA_CABLEWAY_VALUE = "cableway";
export const EXCLUDE_STATIONS_REGEX = /^(?!stations)/;
export const BASE_BRIGHT_NETZKARTE_MAPSET_STYLE = "basemap_bright_mapset";
export const BASE_BRIGHT_NETZKARTE_SATURIERT_MAPSET_STYLE =
  "basemap_saturiert_mapset";
export const BASE_AERIAL_MAPSET_STYLE = "basemap_aerial_ch.sbb.netzkarte";
export const TRAVIC_MAPSET_STYLE = "travic_v2_mapset";
export const AERIAL_MAPSET_STYLE = "aerial";

export const MAPBOX_STYLELAYERS = [
  // When updating the stylelayers, also copy the array into the /scripts/mapbox-stylelayers.test.mjs
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /mapset_stations/,
      name: "haltestellen",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /mapset_station_panels/,
      name: "haltestellenschilder",
    },
    visible: false,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /(mapset_stop_position|mapset_station_platform)/,
      name: "haltekanten",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterAttribute: MAPSET_METADATA_FILTER,
      filterRegex: /path/,
      name: "fusswege",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /mapset_poi/,
      name: "pois",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      fallbackLayer: "pois",
      filterRegex: /access_poi/,
      name: "aufgaenge",
    },
    visible: true,
  },
  {
    children: [
      {
        properties: {
          domains: ["io", "ch"],
          filterAttribute: [GENERAL_METADATA_FILTER, MOT_METADATA_FILTER],
          filterRegex: [
            EXCLUDE_STATIONS_REGEX,
            new RegExp(MOT_METADATA_RAIL_VALUE),
          ],
          name: "bahnlinien",
        },
        visible: true,
      },
      {
        properties: {
          domains: ["io", "ch"],
          filterAttribute: [GENERAL_METADATA_FILTER, MOT_METADATA_FILTER],
          filterRegex: [
            EXCLUDE_STATIONS_REGEX,
            new RegExp(MOT_METADATA_BUS_VALUE),
          ],
          isBusLines: true,
          minZoom: 15,
          name: "buslinien",
        },
        visible: false,
      },
      {
        properties: {
          domains: ["io", "ch"],
          filterAttribute: [GENERAL_METADATA_FILTER, MOT_METADATA_FILTER],
          filterRegex: [
            EXCLUDE_STATIONS_REGEX,
            new RegExp(MOT_METADATA_TRAM_VALUE),
          ],
          name: "tramlinien",
        },
        visible: true,
      },
      {
        properties: {
          domains: ["io", "ch"],
          filterAttribute: [GENERAL_METADATA_FILTER, MOT_METADATA_FILTER],
          filterRegex: [
            EXCLUDE_STATIONS_REGEX,
            new RegExp(MOT_METADATA_SUBWAY_VALUE),
          ],
          name: "ubahnlinien",
        },
        visible: true,
      },
      {
        properties: {
          domains: ["io", "ch"],
          filterAttribute: [GENERAL_METADATA_FILTER, MOT_METADATA_FILTER],
          filterRegex: [
            EXCLUDE_STATIONS_REGEX,
            new RegExp(MOT_METADATA_FERRY_VALUE),
          ],
          name: "schiffslinien",
        },
        visible: true,
      },
      {
        properties: {
          domains: ["io", "ch"],
          filterAttribute: [GENERAL_METADATA_FILTER, MOT_METADATA_FILTER],
          filterRegex: [
            EXCLUDE_STATIONS_REGEX,
            new RegExp(MOT_METADATA_CABLEWAY_VALUE),
          ],
          name: "seilbahnen",
        },
        visible: true,
      },
    ],
    domains: ["io", "ch"],
    properties: {
      name: "verkehrsmittel",
    },
  },
  {
    properties: {
      domains: ["ch", "io"],
      filterRegex: /mapset_station_plan/,
      name: "bahnofplan",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["ch", "io"],
      filterRegex: /mapset_underground/,
      name: "unterfuehrungen",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /mapset_greenareas/,
      name: "pärke",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /mapset_street_names/,
      name: "strassen",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /mapset_water/,
      name: "gewässerlabel",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /mapset_place/,
      name: "ortslabel",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["io", "ch"],
      filterRegex: /mapset_tunnel_labels/,
      name: "tunnelbeschriftungen",
    },
    visible: true,
  },
  // "app" domain
  {
    properties: {
      domains: ["app"],
      filterAttribute: [
        StyleMetadataFilterKey.GeneralClass,
        StyleMetadataFilterKey.GeneralSubclass,
      ],
      filterRegex: [/^label$/, /^streets$/],
      name: "strassen",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["app"],
      filterAttribute: [
        StyleMetadataFilterKey.GeneralClass,
        StyleMetadataFilterKey.GeneralSubclass,
      ],
      filterRegex: [/^label$/, /^place_names$/],
      name: "ortslabel",
    },
    visible: true,
  },

  {
    properties: {
      domains: ["app"],
      filterAttribute: [
        StyleMetadataFilterKey.GeneralClass,
        StyleMetadataFilterKey.GeneralSubclass,
      ],
      filterRegex: [/^label$/, /^water$/],
      name: "gewässerlabel",
    },
    visible: true,
  },

  {
    properties: {
      domains: ["app"],
      filterAttribute: [
        StyleMetadataFilterKey.GeneralClass,
        StyleMetadataFilterKey.GeneralSubclass,
      ],
      filterRegex: [/^label$/, /^tunnel$/],
      name: "tunnelbeschriftungen",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["app"],
      filterAttribute: [
        StyleMetadataFilterKey.GeneralClass,
        StyleMetadataFilterKey.MapsetFilter,
      ],
      filterRegex: [/^streets$/, /^path$/],
      name: "fusswege",
    },
    visible: true,
  },

  {
    properties: {
      domains: ["app"],
      filterAttribute: [
        StyleMetadataFilterKey.GeneralClass,
        StyleMetadataFilterKey.GeneralSubclass,
      ],
      filterRegex: [/^stations$/, /^stations$/],
      name: "haltestellen",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["app"],
      filterAttribute: [
        StyleMetadataFilterKey.GeneralClass,
        StyleMetadataFilterKey.GeneralSubclass,
      ],
      filterRegex: [/^label$/, /^ancillary$/],
      name: "gebirgsnamen",
    },
    visible: true,
  },
  {
    properties: {
      domains: ["app"],
      filterAttribute: [StyleMetadataFilterKey.GeneralClass],
      filterRegex: [/^poi$/],
      name: "pois",
    },
    visible: true,
  },
];

/* Permalink */
export const ROTATION_PARAM = "rotation";
export const DRAW_PARAM = "draw.id";
export const DRAW_OLD_PARAM = "wkp.draw";
export const EXPORT_BTN_PARAM = "export.button";
export const EXPORT_PARAM = "export.bbox";
export const EXPORT_SCALE_PARAM = "export.resolution";
export const EXPORT_MAPLOCKED_PARAM = "export.maplocked";
export const LAYERS_PARAM = "layers";
export const HIDDEN_LAYERS_PARAM = "hidden.layers";
export const BUS_FILTERS_PROVIDER_PARAM = "busfilters.provider";
export const BUS_FILTERS_LINE_PARAM = "busfilters.line";
export const LANG_PARAM = "lang";
export const PARENT_PARAM = "parent";
export const BASE_LAYER_PARAM = "baselayer";
export const VIEW_EXTENT_PARAM = "view.extent"; // @deprecated use max.extent param
export const PREVIEW_BASELAYERS_PARAM = "preview.baselayers"; // boolan to show baselayer switcher in preview
export const SCALEBAR_PARAM = "scalebar"; // boolan to show scalebar in preview
export const NORTH_ARROW_PARAM = "northarrow"; // boolan to show northarrow in preview
export const QR_CODE_PARAM = "qrcode"; // boolan to show qrcode in preview
export const MODE_PARAM = "mode"; // boolan to set to print mode
export const GEOLOCATION_PARAM = "geolocation"; // boolan to set to print mode

/* Save status */
export const SaveStatus = {
  SAVE: "save",
  SAVED: "saved",
  SAVING: "saving",
};
export type SaveStatus = keyof typeof SaveStatus;
export const STATE_SAVING = SaveStatus.SAVING;
export const STATE_SAVED = SaveStatus.SAVED;
export const STATE_SAVE = SaveStatus.SAVE;

/* Zoom limits */
export const MINZOOM_DEFAULT = 0;
export const MAXZOOM_DEFAULT = 20;
export const RANGEZOOM_DEFAULT = [MINZOOM_DEFAULT, MAXZOOM_DEFAULT];

/* Routing */

export const OTHER_MOTS: Mot[] = [
  "tram",
  "coach",
  "subway",
  "gondola",
  "funicular",
  "ferry",
];

export type Mot = GraphhopperRoutingProfile | RoutingMot;

export const RoutingService = {
  Geops: "geops",
  Graphhopper: "graphhopper",
} as const;

export type RoutingService =
  (typeof RoutingService)[keyof typeof RoutingService];

/* Layout export */
export const LOAD_SIGNAL_ID = "load-signal";
export const LAYOUT_MIN_EXPORT_WIDTH = 500; // This is the minimum width for the north arrow, scalebar and copyright to scale properly

/* Image export */
export const IMAGE_EXPORT_URI = "mapset/image_export";
export const KML_EXPORT_URI = "mapset/kml_export";
export const EXPORT_URI_ALLOW_ANONYMOUS = [IMAGE_EXPORT_URI, KML_EXPORT_URI];
export const DEFAULT_EXPORT_SIZE = [300, 300];
export const EXTRA_DATA_SIZE = 80;
export const EXTRA_DATA_SCALE_THRESHOLD = 500;
export const EXTRA_DATA_LOGO_MIN_WIDTH = 43.4;
export const EXTRA_DATA_LOGO_MIN_HEIGHT = 15;
export const EXTRA_DATA_NORTH_ARROW_MIN_SIZE = 40;
export const EXTRA_DATA_COPYRIGHT_MIN_FONTSIZE = 9.1;
export const EXTRA_DATA_COPYRIGHT_DEFAULT_FONTSIZE = 10;
export type ExtraDataQrCodeWidth = {
  id: string;
  value: number;
};

export const EXTRA_DATA_QRCODE_WIDTHS: ExtraDataQrCodeWidth[] = [
  { id: "kein QR-Code", value: 0 },
  { id: "klein", value: EXTRA_DATA_SIZE },
  { id: "mittel", value: EXTRA_DATA_SIZE * 2 },
  { id: "gross", value: EXTRA_DATA_SIZE * 3 },
];

/* Date format */
export const INPUT_DATE_FORMAT = "yyyy-MM-dd";
export const INPUT_TIME_FORMAT = "HH:mm";
export const INPUT_DATE_PATTERN = "d{4}-d{2}-d{2}"; // Used by type="text" fallback
export const INPUT_TIME_PATTERN = "d{2}:d{2}"; // Used by type="text" fallback

/* Plans list */
export const PLANS_LIST_MAX_DISPLAY_COUNT = 50;

/*  MIME types */
export const MIME_TYPE_PNG = "image/png";
export const MIME_TYPE_JPEG = "image/jpeg";
export const MIME_TYPE_PDF = "application/pdf";
export const MIME_TYPE_KML = "application/vnd.google-earth.kml+xml";

/*  Draw types */
export const DRAW_TYPE_PROPERTY = "mapsetDrawType";
export const DRAW_TYPE_ICON = "Icon";
export const DRAW_TYPE_LINE = "Line";
export const DRAW_TYPE_POLYGON = "Polygon";
export const DRAW_TYPE_LABEL = "Label";
export const DRAW_TYPE_PICTURE = "Picture";
export const DRAW_TYPE_CIRCLE = "Circle";
export const DRAW_TYPE_BOX = "Box";
export const DRAW_TYPE_TRIANGLE = "Triangle";
export const DRAW_TYPE_ROUTE = "Route";

export const DRAW_TYPE_GROUP_PROPERTY = "mapsetDrawTypeGroup";
export const DRAW_TYPE_GROUP_AREA = "area";
export const DRAW_TYPE_GROUP_LINE = "line";
export const DRAW_TYPE_GROUP_POINT = "point";

export const DRAW_TYPES_BY_GROUP = {
  [DRAW_TYPE_GROUP_AREA]: [
    DRAW_TYPE_POLYGON,
    DRAW_TYPE_BOX,
    DRAW_TYPE_TRIANGLE,
    DRAW_TYPE_CIRCLE,
  ],
  [DRAW_TYPE_GROUP_LINE]: [DRAW_TYPE_LINE, DRAW_TYPE_ROUTE],
  [DRAW_TYPE_GROUP_POINT]: [DRAW_TYPE_ICON, DRAW_TYPE_PICTURE],
};

export const DRAW_TYPES = [
  DRAW_TYPE_ICON,
  DRAW_TYPE_LINE,
  DRAW_TYPE_POLYGON,
  DRAW_TYPE_LABEL,
  DRAW_TYPE_PICTURE,
  DRAW_TYPE_CIRCLE,
  DRAW_TYPE_BOX,
  DRAW_TYPE_TRIANGLE,
  DRAW_TYPE_ROUTE,
];

// Export types
export const exportTypes = ["pdf", "image", "kml"];

// Broadcast messages
export const BROADCAST_MSG_LOGIN = "mapset-login";
export const BROADCAST_MSG_LOGOUT = "mapset-logout";
